import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CATEGORIES } from '../lib/categories';
const PADDING = 56; // espace pour labels autour octogone
/**
 * Ordre clockwise depuis north : memory (top), executive (TR), social (R),
 * language (BR), speed (bottom), attention (BL), motor (L, pending),
 * perception (TL, pending). Cohérence pédagogique : sémantique gauche/droite
 * (perception ↔ social → input/output cognitif), top/bottom (memory ↔ speed
 * → stockage/vitesse).
 */
const AXIS_ORDER = [
    'memory',
    'executive',
    'social',
    'language',
    'speed',
    'attention',
    'motor',
    'perception',
];
function buildAxes(radius, center) {
    const orderedCats = AXIS_ORDER.map((slug) => CATEGORIES.find((c) => c.slug === slug));
    return orderedCats.map((cat, i) => {
        // -Math.PI/2 = top (12 o'clock), then clockwise
        const angle = -Math.PI / 2 + (i * 2 * Math.PI) / AXIS_ORDER.length;
        const labelRadius = radius + 22;
        return {
            cat,
            angle,
            labelX: center + Math.cos(angle) * labelRadius,
            labelY: center + Math.sin(angle) * labelRadius,
        };
    });
}
export function RadarProfile({ scores, size = 280 }) {
    const { t } = useTranslation(['library']);
    const titleId = useId();
    const center = size / 2;
    const maxRadius = center - PADDING;
    const axes = useMemo(() => buildAxes(maxRadius, center), [maxRadius, center]);
    // Polygone octogone reference (100% radius) — gridlines.
    const refLevels = [0.25, 0.5, 0.75, 1];
    const referencePolygons = refLevels.map((level) => axes
        .map((a) => {
        const r = maxRadius * level;
        const x = center + Math.cos(a.angle) * r;
        const y = center + Math.sin(a.angle) * r;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
        .join(' '));
    // Polygone score réel utilisateur (path D pour stroke + fill cyanotype).
    const scoreVertices = axes.map((a) => {
        const isPending = a.cat.status === 'pending';
        const pct = isPending ? 0 : (scores[a.cat.slug]?.pct ?? 0);
        const r = (Math.max(0, Math.min(100, pct)) / 100) * maxRadius;
        const x = center + Math.cos(a.angle) * r;
        const y = center + Math.sin(a.angle) * r;
        return { x, y };
    });
    const scorePath = scoreVertices
        .map((v) => `${v.x.toFixed(1)},${v.y.toFixed(1)}`)
        .join(' ');
    return (_jsxs("div", { className: "flex flex-col items-center gap-4", children: [_jsxs("svg", { width: size, height: size, viewBox: `0 0 ${size} ${size}`, role: "img", "aria-labelledby": titleId, className: "block h-auto w-full max-w-full", children: [_jsx("title", { id: titleId, children: t('library:wiki.section_canonical') }), referencePolygons.map((points, i) => (_jsx("polygon", { points: points, fill: "none", stroke: "#2D5A8A", strokeWidth: 1, opacity: i === referencePolygons.length - 1 ? 0.5 : 0.18 }, i))), axes.map((a) => {
                        const x = center + Math.cos(a.angle) * maxRadius;
                        const y = center + Math.sin(a.angle) * maxRadius;
                        const isPending = a.cat.status === 'pending';
                        return (_jsx("line", { x1: center, y1: center, x2: x, y2: y, stroke: "#2D5A8A", strokeWidth: 1, opacity: isPending ? 0.18 : 0.35, strokeDasharray: isPending ? '3 3' : undefined }, `axis-${a.cat.slug}`));
                    }), _jsx("polygon", { points: scorePath, fill: "rgba(45,90,138,0.18)", stroke: "#0A2640", strokeWidth: 1.6, strokeLinejoin: "round", className: "transition-all duration-[600ms] ease-out" }), axes.map((a, i) => {
                        if (a.cat.status === 'pending')
                            return null;
                        const v = scoreVertices[i];
                        return (_jsx("circle", { cx: v.x, cy: v.y, r: 3, fill: "#0A2640" }, `dot-${a.cat.slug}`));
                    }), axes.map((a) => {
                        const isPending = a.cat.status === 'pending';
                        let textAnchor = 'middle';
                        // Snap horizontal anchor selon position relative au centre
                        if (a.labelX < center - 4)
                            textAnchor = 'end';
                        else if (a.labelX > center + 4)
                            textAnchor = 'start';
                        return (_jsx("text", { x: a.labelX, y: a.labelY, textAnchor: textAnchor, dominantBaseline: "middle", fontFamily: "JetBrains Mono, ui-monospace, monospace", fontSize: 9, letterSpacing: "0.1em", fill: "currentColor", className: isPending ? 'text-cyan-text-faint' : 'text-cyan-text-secondary', style: { textTransform: 'uppercase' }, children: t(a.cat.labelKey) }, `label-${a.cat.slug}`));
                    })] }), _jsxs("div", { className: "flex items-center gap-5 text-[9px] uppercase tracking-[0.15em] font-mono text-cyan-text-faint", children: [_jsxs("span", { className: "flex items-center gap-1.5", children: [_jsx("span", { "aria-hidden": true, className: "inline-block h-[1px] w-3 bg-cyan-text" }), t('library:wiki.section_canonical')] }), _jsxs("span", { className: "flex items-center gap-1.5", children: [_jsx("span", { "aria-hidden": true, className: "inline-block h-[1px] w-3 border-t border-dashed border-cyan-text-faint" }), t('library:coming_soon_m5')] })] })] }));
}
//# sourceMappingURL=RadarProfile.js.map